import { Fragment, useEffect, useRef, useState } from 'react';
import { useSigner, SignerEditor } from '../components/Signer.jsx';
import { MOCK } from '../lib/supabase.js';
import * as db from '../lib/data.js';
import { DOC_HEAD_TH } from '../lib/company.js';
import NameTh from '../components/NameTh.jsx';
import { COMMON_COUNTRIES } from '../components/Modals.jsx';
import { buildPermitIndex, checkCard, sheetAgency } from '../lib/permit-flags.js';

// รูปจาก Supabase = cross-origin → Chrome มักไม่พิมพ์ติดตอน print/PDF
// แก้: แปลงเป็น data URL (ฝังในหน้า) ก่อนสั่งพิมพ์ แล้วรูปจะติดชัวร์
const blobToDataUrl = (blob) => new Promise((res, rej) => {
  const r = new FileReader(); r.onload = () => res(r.result); r.onerror = rej; r.readAsDataURL(blob);
});

// เอกสารข้อมูลสินค้าเพื่อยื่นขอใบอนุญาต/การรับรองต่อหน่วยงาน (สมอ. / เกษตร / อื่น ๆ)
// ดึงข้อมูลจาก product_classification + เพิ่มรูป/แนบไฟล์ประกอบคำขอได้จากหน้านี้ — พิมพ์ A4 ได้เลย
//
// หน้าตาใบ = ใบ PDF ที่ยื่น สมอ. รอบ SP-261015 (30 ต.ค. 69 user: "อยากให้รูปแบบของใบในแอพ หน้าตาเหมือนที่ไฟล์เลย
//   สวยมาก ข้อมูลครบ") — หัวบริษัทภาษาไทย · หัวเรื่องกลางหน้า · "เรียน" หน่วยงาน · กรอบเทาเลขใบขน/Invoice ·
//   รูป 3 ช่องมีคำอธิบายใต้รูป · ตารางหัวเทาแถวเดียวจบ (ข้อมูลสินค้า + ที่กรอกในหน้านี้) · ลงชื่อชิดขวา
//   แผ่นบนจอ = ของที่จะพิมพ์ (ช่องกรอก/ปุ่ม ติด noprint) · แถวที่ยังว่าง = เห็นบนจอแต่ไม่พิมพ์

const AGENCIES = [
  { id: 'tisi', short: 'สมอ. (มอก.)', name: 'สำนักงานมาตรฐานผลิตภัณฑ์อุตสาหกรรม (สมอ.)' },
  { id: 'doa', short: 'เกษตร (กักพืช)', name: 'กรมวิชาการเกษตร / ด่านตรวจพืช' },
  { id: 'other', short: 'หน่วยงานอื่น ๆ', name: 'หน่วยงานราชการที่เกี่ยวข้อง' },
];
const SHEET_TITLE = 'รายละเอียดสินค้าประกอบการพิจารณา';

// แถวที่ "กรอกในหน้านี้" (permit_docs field 'note') — อยู่ในตารางเดียวกับข้อมูลสินค้า ตำแหน่งตามใบ PDF
//   (spec = แถว "ขนาด / น้ำหนัก" · catalog = "แหล่งข้อมูล") · ขึ้นบรรทัดใหม่ = ข้อใหม่ (พิมพ์เป็นรายการจุด)
//   แนบไฟล์ตัวจริงต่อแถวได้ถ้ามี (ไม่บังคับ — ไฟล์อยู่ในซอง ZIP)
const DOC_KINDS = [
  { k: 'spec', label: 'ขนาด / น้ำหนัก', ph: 'เช่น ขนาด 93 × 86 × 21 มม.  (กด Enter ขึ้นบรรทัดใหม่ = ข้อถัดไป เช่น น้ำหนัก 41 กรัม)' },
  { k: 'catalog', label: 'แหล่งข้อมูล', ph: 'ลิงก์หน้าสินค้าของผู้ผลิต หรือแคตตาล็อก (ชื่อ/หน้า)' },
  { k: 'manual', label: 'คู่มือ / ข้อควรระวัง', ph: 'วิธีใช้โดยย่อ / ข้อควรระวัง (ถ้ามี)' },
  { k: 'test', label: 'ผลทดสอบ / มาตรฐาน', ph: 'ผลทดสอบ/มาตรฐานที่ผ่าน (แนบไฟล์ตัวจริงได้ถ้าหน่วยงานขอสำเนา)' },
  { k: 'other', label: 'ข้อมูลเพิ่มเติม', ph: 'ข้อมูลอื่น ๆ ที่ต้องแจ้งหน่วยงาน' },
];

// อ้างอิงการนำเข้า (ใบขน · Invoice · ใบตราส่ง) — พิมพ์เป็นกรอบเทาใต้ "เรียน" · จำนวน = แถวในตาราง
//   ⚠ เป็นของ "รอบนำเข้า" ไม่ใช่ของสินค้า แต่เก็บในใบสินค้า (permit_docs kind 'ref' + เวลาบันทึก at)
//   → ยื่นรอบใหม่ต้องล้าง/แก้ ไม่งั้นเลขใบขนเก่าติดไปกับใบใหม่ — บนจอจึงโชว์วันที่บันทึก + ปุ่มล้างเสมอ
const REF_FIELDS = [
  { f: 'ref_decl', label: 'เลขที่ใบขนสินค้า', ph: 'เช่น PPKF110305563' },
  { f: 'ref_line', label: 'รายการที่ (ในใบขน)', ph: 'เช่น 155 และ 156' },
  { f: 'ref_inv', label: 'Invoice เลขที่ (วันที่)', ph: 'เช่น TH-8074000-2616-A (23/10/2026)' },
  { f: 'ref_bl', label: 'ใบตราส่ง (AWB / B/L)', ph: 'เช่น AWB 20571430660' },
  { f: 'ref_arrive', label: 'วันนำเข้า / ด่าน', ph: 'เช่น 30/10/2569 ท่าอากาศยานสุวรรณภูมิ' },
  { f: 'ref_qty', label: 'จำนวนที่นำเข้า', ph: 'เช่น 180 ชิ้น' },
];
const NO_POWER_TEXT = 'ไม่ใช้ไฟฟ้า · ไม่มีส่วนประกอบไฟฟ้าหรือแบตเตอรี่';

// ผลจากหน่วยงาน (สมอ. ส่วนใหญ่ = หนังสือรับรองว่า "ไม่เข้าข่ายบังคับ มอก. → นำเข้าได้เลย")
const LIC_STATUS = [
  'ไม่เข้าข่ายบังคับ — นำเข้าได้เลย',
  'ได้รับใบอนุญาต / ใบรับรองแล้ว',
  'อยู่ระหว่างยื่นขอ',
  'ยังไม่ดำเนินการ',
];
const CLEARED = LIC_STATUS[0];   // สถานะที่ = ผ่านแล้ว นำเข้าได้

const isImg = (it) => (it.mime || '').startsWith('image/') || /\.(jpe?g|png|gif|webp)$/i.test(it.name || it.url || '');
// คำอธิบายใต้รูปที่ "พิมพ์ได้" — ไม่เอาป้าย 'PD' (รูปที่ดึงจากทะเบียน) และชื่อไฟล์ (หน้าแก้ไขสินค้าเก็บชื่อไฟล์ไว้ใน caption)
const capOf = (p) => {
  const c = String(p?.caption ?? '').trim();
  return !c || c === 'PD' || /\.(jpe?g|png|gif|webp|heic|heif|avif)$/i.test(c) ? '' : c;
};
// รูปที่ใส่ในใบยื่น = ทุกรูป ยกเว้นที่กด "ไม่ใส่ในใบ" (permit: false — รูปยังอยู่ในสินค้า)
const onSheet = (p) => !!p?.url && p.permit !== false;
// พิกัด 11 หลัก "3924.10.99.000" → ["3924.10.99", "000"] พิมพ์แบบใบขน (รหัสสถิติแยกวงเล็บ)
const hsParts = (hs) => {
  const m = /^(\d{4}\.\d{2}\.\d{2})\.(\d{3})$/.exec(String(hs ?? '').trim());
  return m ? [m[1], m[2]] : [String(hs ?? '').trim(), null];
};
// ข้อความหลายข้อ → รายการจุดแบบใบ PDF · ข้อเดียว = ข้อความเฉย ๆ
const listOf = (s, re) => {
  const xs = String(s ?? '').split(re).map(x => x.trim()).filter(Boolean);
  return xs.length > 1 ? <ul className="pd-ul">{xs.map((x, i) => <li key={i}>{x}</li>)}</ul> : (xs[0] ?? '');
};
// ใบตราส่งพิมพ์ตามที่กรอก — ถ้ากรอกแค่ตัวเลข เติมคำนำหน้าให้รู้ว่าเลขอะไร
const blText = (v) => (/^(h|m)?(awb|b\/?l)\b|^ใบตราส่ง/i.test(v) ? v : `ใบตราส่ง ${v}`);

// ไฟล์แนบ 1 ชิ้น — รูปโชว์รูปย่อ, ไฟล์อื่นโชว์ไอคอนเอกสาร + ชื่อ + เปิด + ลบ
// it.pf = แถว product_file (ไฟล์ส่วนตัว deploy-37) → เปิดผ่าน onOpen (ลิงก์ชั่วคราว) + ติ๊กใส่ซองได้
//   ไฟล์ที่ไม่ได้ติ๊ก = ไม่พิมพ์ ไม่เข้าซอง ZIP (จางบนจอให้เห็นว่าไม่ได้ส่ง)
// ⚠ ไฟล์ส่วนตัว "ไม่โชว์รูปย่อ" โดยตั้งใจ (รีวิว 26 ต.ค.):
//   · ลิงก์ชั่วคราวหมดใน 1 ชม. — เปิดหน้านี้ค้างไว้แล้วกดพิมพ์ storage ตอบ 400 เป็น JSON (CORS เปิด fetch ไม่ throw)
//     → หน้า error ถูกพิมพ์แทนรูป และถูกจำไว้ พิมพ์ซ้ำกี่ครั้งก็เสีย
//   · รูปย่อ 32px บนใบที่พิมพ์ไม่มีประโยชน์กับหน่วยงาน — ตัวไฟล์จริงอยู่ในซอง ZIP
//   · ไม่ต้องดาวน์โหลดรูปเต็มขนาด (สูงสุด 20 MB) ทุกครั้งที่เปิดหน้า แค่เพื่อวาดรูปย่อ
function FileChip({ it, onRemove, resolve, onOpen, onToggle, confirmRemove }) {
  const [asking, setAsking] = useState(false);
  const open = () => (onOpen ? onOpen() : it.url && window.open(it.url, '_blank', 'noopener'));
  const off = it.pf && !it.pf.for_permit;
  return (
    <div className={`fchip${onToggle ? ' wide' : ''}${off ? ' noprint off' : ''}`}>
      {isImg(it) && !it.pf
        ? <img className="fchip-th" src={(resolve && it.url && resolve(it.url)) || it.url} alt="" onClick={open} style={{ cursor: it.url ? 'pointer' : 'default' }}/>
        : <span className="fchip-ic" onClick={open}>
            <svg viewBox="0 0 24 24" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" fill="none">
              <path d="M13 3H7a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h10a2 2 0 0 0 2-2V8z"/><path d="M13 3v5h5"/></svg>
          </span>}
      <span className="fchip-nm" onClick={open} title={it.name}>{it.name || 'ไฟล์แนบ'}</span>
      {onToggle && (
        <label className="fchip-pick noprint" title="ติ๊ก = พิมพ์ติดใบนี้และใส่ในซอง ZIP ที่ยื่นหน่วยงาน">
          <input type="checkbox" checked={!!it.pf?.for_permit} onChange={onToggle}/>ใส่ในซองยื่น
        </label>
      )}
      {onOpen
        ? <button type="button" className="fchip-open fchip-btn noprint" onClick={open}>เปิด</button>
        : it.url && <a className="fchip-open noprint" href={it.url} target="_blank" rel="noopener">เปิด</a>}
      {onRemove && (asking
        ? <span className="fchip-ask noprint">
            เอาออก?
            <button type="button" className="fchip-yes" onClick={() => { setAsking(false); onRemove(); }}>เอาออก</button>
            <button type="button" className="fchip-no" onClick={() => setAsking(false)}>ไม่</button>
          </span>
        : <button type="button" className="fchip-x noprint" title="เอาไฟล์ออก"
            onClick={() => (confirmRemove ? setAsking(true) : onRemove())}>✕</button>)}
    </div>
  );
}

// พิมพ์ "จบในหน้า A4 เดียว" (user 30 ต.ค. 69: "เอกสารควรจบในหนึ่งหน้า A4 ไม่ควรยืดยาว")
//   ความสูงพื้นที่พิมพ์ = A4 297 มม. − ขอบบน/ล่าง 12 มม. (ตรงกับ @page ใน styles.css) · หน่วย CSS px (96/นิ้ว)
const PRINT_H_PX = (297 - 24) * 96 / 25.4;
const ccName = (c) => COMMON_COUNTRIES.find(([k]) => k === c)?.[1] ?? c;
// ค่าที่มีแค่ขีด/ช่องว่าง = ไม่มีข้อมูล — power_spec ของสินค้า 206 ใบเป็น "-" (แปลว่าไม่ใช้ไฟ) ห้ามพิมพ์เป็นแถว "กำลังไฟ: -"
//   และห้ามนับว่าเป็นสินค้าไฟฟ้าตอนเลือกหน่วยงานตั้งต้น (รีวิว 30 ต.ค. 69)
const hasVal = (v) => (typeof v === 'string' ? !/^[\s\-–—]*$/.test(v) : !!v);

export default function PermitSheet({ cls, onBack, canCost = false }) {
  // หน่วยงานที่เลือกไว้ จำต่อสินค้าใน permit_docs (field 'agency') — เดิมเปิดทุกครั้งเป็น "เกษตร" ถ้าไม่มีกำลังไฟ
  //   สินค้าที่ NSW เตือน สมอ. (เช่น 3924.10.99 ไม่ใช้ไฟฟ้า) ต้องเปลี่ยนเองทุกครั้ง
  const savedAgency = (Array.isArray(cls?.permit_docs) ? cls.permit_docs : []).find(d => d?.field === 'agency')?.text;
  const [agency, setAgency] = useState(() => (AGENCIES.some(x => x.id === savedAgency) ? savedAgency : (hasVal(cls?.power_spec) ? 'tisi' : 'doa')));
  // ยังไม่เคยเลือกเอง → ดูมาตรการของพิกัด: มี permit_tisi (เช่น 3924.10.99 · 9617.00.10 ที่ NSW เตือน สมอ.) = เปิดเป็น สมอ.
  const agencyPicked = useRef(AGENCIES.some(x => x.id === savedAgency));
  useEffect(() => {
    if (MOCK || agencyPicked.current || !cls?.hs_code) return;
    let alive = true;
    // ใช้ตัวตรวจชุดเดียวกับกล่องเรื่องใบอนุญาต — เดิมเทียบพิกัดตรงตัว ใบ 73239310 เลยเปิดเป็นเกษตรทั้งที่ป้ายบอก สมอ.
    db.loadPermitRules().then(rules => {
      if (!alive || agencyPicked.current) return;
      const a = sheetAgency(checkCard(cls, buildPermitIndex(rules)));
      if (a) setAgency(a);
    }).catch(() => {});   // อ่านกฎพิกัดไม่ได้ = ใช้ค่าตั้งต้นเดิม ไม่ต้องเตือน (เลือกเองได้เสมอ)
    return () => { alive = false; };
  }, [cls?.hs_code]);
  const [signer, reloadSigner] = useSigner();
  const [photos, setPhotos] = useState(cls?.photos ?? []);
  // ชุดรูปล่าสุดเสมอ — อัปรูป (HEIC→JPEG + storage) ใช้เวลาหลายวินาที ระหว่างนั้นยังพิมพ์คำอธิบาย/กดไม่ใส่ในใบ/เอาออกได้
  //   ถ้าต่อรูปใหม่เข้ากับ photos ของตอนกดอัป จะทับการแก้ระหว่างนั้นทิ้งเงียบ ๆ (รีวิว 30 ต.ค. 69 — ทำซ้ำได้ใน harness)
  //   → ทุกการแก้อ่าน/เขียนผ่าน ref นี้ (photos ใน state ไว้วาดจอเท่านั้น)
  const photosRef = useRef(photos);
  const [docs, setDocs] = useState(() => MOCK ? [
    { id: 'note-spec', kind: 'spec', field: 'note', text: 'ขนาดพับ 60 × 15 × 15 ซม.\nน้ำหนัก 2.4 กก.' },
    { id: 'note-catalog', kind: 'catalog', field: 'note', text: 'รุ่น TC-095 ซีรีส์ Wingfort — ดูแคตตาล็อก 2024 หน้า 12' },
    { id: 'no_power-meta', kind: 'meta', field: 'no_power', text: '1' },
    { id: 'ref_decl-ref', kind: 'ref', field: 'ref_decl', text: 'A0011234567890' },
    { id: 'ref_inv-ref', kind: 'ref', field: 'ref_inv', text: 'INV-2025-001 (12/03/2025)' },
    { id: 'ref_qty-ref', kind: 'ref', field: 'ref_qty', text: '40 ชิ้น' },
    { id: 't', kind: 'test', field: 'tisi', text: 'มอก. 2432-2555' },
    { id: 'd1', kind: 'test', name: 'test_report.pdf', mime: 'application/pdf', url: '' },
    { id: 'lic_status', kind: 'license', field: 'lic_status', text: 'ไม่เข้าข่ายบังคับ — นำเข้าได้เลย' },
    { id: 'lic_no', kind: 'license', field: 'lic_no', text: 'อก 0709/1234' },
    { id: 'lic_issued', kind: 'license', field: 'lic_issued', text: '2025-03-15' },
    { id: 'licf', kind: 'license', name: 'หนังสือรับรอง_สมอ.pdf', mime: 'application/pdf', url: '' },
  ] : (cls?.permit_docs ?? []));
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState(null);
  const [okMsg, setOkMsg] = useState(null);   // แจ้งผลสำเร็จ (เอาไฟล์ออก / ดาวน์โหลดซองแล้ว)
  const [zipBusy, setZipBusy] = useState(false);   // ต้องอยู่ก่อน "if (!cls) return" (กฎ hooks)
  const [rmAsk, setRmAsk] = useState(null);         // url ของรูปที่กำลังถาม "เอาออกจากสินค้า?"
  const [refAsk, setRefAsk] = useState(false);      // ถาม "ล้างเลขอ้างอิง?"
  const [refEpoch, setRefEpoch] = useState(0);      // ช่องกรอกอ้างอิงเป็น defaultValue → ล้างแล้วต้อง remount ให้ช่องว่างจริง
  const fileInput = useRef(null);
  const target = useRef('photo');   // 'photo' | kind
  const canEdit = !MOCK && !!cls?.id;
  // ไฟล์ของสินค้า (product_file · ส่วนตัว) — ใช้ชุดเดียวกับหน้าแก้ไขสินค้า · เฉพาะทีมที่ดูต้นทุนได้ (RLS deploy-37)
  const [files, setFiles] = useState([]);
  // แนบเอกสารได้เมื่อโหลดรายการสำเร็จแล้วเท่านั้น — ยังไม่รัน deploy-37 = อัปแล้วเหลือไฟล์กำพร้าใน storage
  const [filesReady, setFilesReady] = useState(false);
  const canFiles = canEdit && canCost && filesReady;
  useEffect(() => {
    if (!canEdit || !canCost) return;
    let alive = true;
    db.listProductFiles(cls.id)
      .then(rows => { if (alive) { setFiles(rows); setFilesReady(true); } })
      .catch(er => {
        if (!alive) return;
        // ยังไม่รัน deploy-37 → เงียบ (ไม่มีไฟล์ · ปุ่มแนบเอกสารไม่โชว์) — โผล่เองเมื่อรัน SQL แล้ว
        const missing = er?.code === 'PGRST205' || er?.code === '42P01' || /ib_product_file/i.test(er?.message ?? '');
        if (!missing) setErr(er.message ?? String(er));
      });
    return () => { alive = false; };
  }, [cls?.id, canEdit, canCost]);

  // พิมพ์: แปลงรูป cross-origin → data URL ก่อน แล้วสั่ง window.print() หลังรีเรนเดอร์
  const [imgData, setImgData] = useState({});
  const [printing, setPrinting] = useState(false);
  const resolveImg = (url) => imgData[url] ?? url;

  // ย่อให้พอดีหน้าเดียวตอนพิมพ์ — วัดความสูงแบบที่จะพิมพ์ (กว้างเท่ากระดาษ ซ่อนของบนจอ) ตอน beforeprint
  //   เกินหน้า → ตั้ง --fit ให้ zoom ใน @media print · ไม่เกิน = 1 (ขนาดจริง)
  //   ใช้ beforeprint ไม่ใช่แค่ในปุ่ม: กด ⌘P เองก็ได้ผลเดียวกัน · ต้องอยู่ก่อน "if (!cls) return" (กฎ hooks)
  const sheetRef = useRef(null);
  useEffect(() => {
    const fit = () => {
      const el = sheetRef.current;
      if (!el) return;
      // ขนาดตัวอักษร A−/A+ ของแอพ (= zoom ของ .wrap) ไม่ตามไปตอนพิมพ์ (@media print .wrap{zoom:1}) แต่ตอนวัดยังติดอยู่
      //   → ข้อความตัดบรรทัดต่างจากกระดาษ วัดได้เตี้ยกว่าจริง แล้วช่องลงชื่อหล่นไปหน้า 2 (รีวิว 30 ต.ค. 69 · ทำซ้ำได้ที่ 110–160%)
      //   → ตั้ง zoom แอพเป็น 1 ระหว่างวัด แล้วคืนค่าเดิม
      const root = document.documentElement;
      const appZoom = root.style.getPropertyValue('--app-zoom');
      root.style.setProperty('--app-zoom', '1');
      el.style.removeProperty('--fit');
      el.classList.add('a4-measure');
      const h = el.scrollHeight;
      if (h > PRINT_H_PX - 4) {   // เผื่อ 4px เท่ากับตอนย่อ — ค่าที่เกือบเต็มหน้าพอดีเคยหล่นหน้า 2
        // zoom ทำให้เนื้อหาจัดบรรทัดในความกว้าง 186mm/fit (กว้างขึ้น) → ความสูงลดเร็วกว่า fit ตรง ๆ
        //   ถ้าใช้ PRINT_H/h เฉย ๆ จะย่อเกินจำเป็น (ใบยาวพิมพ์ตัวเล็ก ~5.7pt ทั้งที่พอใส่ได้ ~6.9pt — รีวิว 30 ต.ค. 69)
        //   → หาค่าที่ใหญ่ที่สุดที่ยังพอดีหน้า · ความสูงจริง = fit × scrollHeight (scrollHeight ไม่รวม zoom ของตัวเอง
        //     และไม่โดน zoom ของแอพบนจอ .wrap แบบ getBoundingClientRect)
        const fits = (z) => { el.style.setProperty('--fit', String(z)); return z * el.scrollHeight <= PRINT_H_PX - 4; };
        let lo = Math.floor((PRINT_H_PX / h) * 100) / 100 - 0.01, hi = 1;
        for (let i = 0; i < 8 && hi - lo > 0.01; i++) {
          const mid = Math.floor(((lo + hi) / 2) * 100) / 100;
          if (fits(mid)) lo = mid; else hi = mid;
        }
        el.style.setProperty('--fit', String(lo));
      }
      el.classList.remove('a4-measure');   // --fit ค้างไว้ใช้ตอนพิมพ์ — บนจอไม่มีอะไรอ่าน --fit นอก .a4-measure
      if (appZoom) root.style.setProperty('--app-zoom', appZoom); else root.style.removeProperty('--app-zoom');
    };
    window.addEventListener('beforeprint', fit);
    return () => window.removeEventListener('beforeprint', fit);
  }, []);

  if (!cls) return null;
  const a = AGENCIES.find(x => x.id === agency) ?? AGENCIES[2];

  const pick = (t) => {
    target.current = t;
    if (fileInput.current) {
      fileInput.current.accept = t === 'photo' ? 'image/*' : db.PRODUCT_FILE_ACCEPT;
      fileInput.current.click();
    }
  };
  const onFile = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file || !canEdit || !canCost) return;
    setBusy(true); setErr(null);
    try {
      if (target.current === 'photo') {
        const url = await db.uploadPermitFile(cls.id, file);
        await savePhotos([...photosRef.current, { url }]);
      } else {
        // 🚨 เอกสารห้ามใช้ uploadPermitFile (bucket สาธารณะ) — ไปที่เก็บส่วนตัวเสมอ (deploy-37)
        if (!canFiles) return;
        const row = await db.uploadProductFile(cls.id, file, { kind: target.current, forPermit: target.current !== 'license' });
        if (row) setFiles(prev => [row, ...prev]);
      }
    } catch (er) { setErr(er.message ?? String(er)); }
    setBusy(false);
  };
  // ── รูป: แก้ทั้งชุดแล้วบันทึก (photos เป็น jsonb ทั้งก้อน) ──
  const savePhotos = async (next) => {
    photosRef.current = next;
    setPhotos(next);
    try { await db.saveClassPhotos(cls.id, next); } catch (er) { setErr(er.message ?? String(er)); }
  };
  const removePhoto = (i) => { setRmAsk(null); savePhotos(photosRef.current.filter((_, idx) => idx !== i)); };
  // คำอธิบายใต้รูป — ไม่เปลี่ยน = ไม่เขียน (คลิกผ่านช่องเฉย ๆ ไม่ต้องยิง DB)
  const setCaption = (i, v) => {
    const cur = photosRef.current, t = (v || '').trim();
    if (!cur[i] || t === capOf(cur[i])) return;
    savePhotos(cur.map((p, idx) => (idx === i ? { ...p, caption: t } : p)));
  };
  // ใส่/ไม่ใส่ในใบยื่น — รูปยังอยู่ในสินค้าเสมอ (หน้าสินค้า/ใบ dossier ยังใช้รูปแรกเป็นรูปหลัก)
  const setOnSheet = (i, on) => {
    const cur = photosRef.current;
    if (!cur[i]) return;
    const { permit, ...rest } = cur[i];
    savePhotos(cur.map((p, idx) => (idx === i ? (on ? rest : { ...rest, permit: false }) : p)));
  };
  const removeDoc = async (id) => {
    const next = docs.filter(d => d.id !== id);
    setDocs(next);
    try { await db.savePermitDocs(cls.id, next); } catch (er) { setErr(er.message ?? String(er)); }
  };
  // ── ไฟล์ส่วนตัว ──
  const openFile = (f) => {
    const w = window.open('', '_blank');
    if (w) w.opener = null;   // แท็บที่เปิดไฟล์ต้องสั่งหน้าแอพนี้ไม่ได้
    db.productFileOpenUrl(f)
      .then(url => { if (w) w.location.href = url; else window.open(url, '_blank', 'noopener'); })
      .catch(er => { if (w) w.close(); setErr(`เปิดไฟล์ไม่สำเร็จ: ${er.message ?? String(er)}`); });
  };
  const togglePermit = async (f) => {
    const want = !f.for_permit;
    setFiles(prev => prev.map(x => (x.id === f.id ? { ...x, for_permit: want } : x)));
    try { await db.updateProductFile(f.id, { for_permit: want }); }
    catch (er) {
      setFiles(prev => prev.map(x => (x.id === f.id ? { ...x, for_permit: f.for_permit } : x)));
      setErr(er.message ?? String(er));
    }
  };
  // เอาออกได้เฉพาะไฟล์ที่แนบจากหน้านี้ — ไฟล์อ้างอิงจากใบสินค้าแค่ติ๊กออกจากซอง (ลบที่หน้าแก้ไขสินค้า)
  const removeFile = async (f) => {
    setFiles(prev => prev.filter(x => x.id !== f.id));
    try {
      await db.removeProductFile(f.id);
      setOkMsg(`เอา "${f.file_name}" ออกแล้ว — ถ้าเอาออกผิด แอดมินกู้คืนได้ที่ ตั้งค่า › ถังขยะ`);
    } catch (er) { setFiles(prev => [f, ...prev]); setErr(er.message ?? String(er)); }
  };
  // แถว product_file → รูปทรงที่ FileChip ใช้
  const chipOf = (f) => ({ id: f.id, name: f.file_name, mime: f.mime_type, pf: f });
  const fileChip = (f, removable) => (
    <FileChip key={f.id} it={chipOf(f)} onOpen={() => openFile(f)} confirmRemove
      onToggle={canFiles ? () => togglePermit(f) : null} onRemove={removable && canFiles ? () => removeFile(f) : null}/>
  );
  const shown = photos.map((p, i) => ({ p, i })).filter(({ p }) => onSheet(p));
  const hidden = photos.map((p, i) => ({ p, i })).filter(({ p }) => p?.url && !onSheet(p));
  // แปลงรูปที่จะพิมพ์เป็น data URL (ถ้ายังไม่ได้แปลง) + รอฟอนต์ แล้วค่อยสั่งพิมพ์ → รูป/ตัวอักษรติดชัวร์
  const doPrint = async () => {
    const urls = [...new Set([
      ...shown.map(({ p }) => p.url),
      ...docs.filter(d => d.url && !d.field && isImg(d)).map(d => d.url),
    ].filter(u => u && !u.startsWith('data:')))];
    const missing = urls.filter(u => !imgData[u]);
    setPrinting(true); setErr(null);
    const cache = { ...imgData };
    await Promise.all(missing.map(async (url) => {
      // ⚠ fetch ไม่ throw เมื่อได้ 4xx — ต้องเช็คเอง ไม่งั้นหน้า error ถูกจำเป็น "รูป" แล้วพิมพ์เสียทุกครั้ง
      try {
        const res = await fetch(url);
        const blob = res.ok ? await res.blob() : null;
        if (blob?.type?.startsWith('image/')) cache[url] = await blobToDataUrl(blob);
      } catch { /* โหลดไม่ได้ = ใช้ url เดิม (อาจไม่ติดตอนพิมพ์) */ }
    }));
    // ใบนี้ใช้ฟอนต์ Sarabun (Google Fonts) — ยังโหลดไม่เสร็จตอนสั่งพิมพ์ = ใบออกมาเป็นฟอนต์สำรอง ตัดบรรทัดต่างจากที่เห็น
    try { await document.fonts?.ready; } catch { /* เบราว์เซอร์เก่า — พิมพ์ต่อได้ */ }
    if (missing.length) setImgData(cache);
    setPrinting(false);
    // setTimeout (ไม่ใช่ rAF ที่หยุดตอนแท็บไม่โฟกัส) → React commit + <img> เป็น data URL แล้วค่อยพิมพ์
    setTimeout(() => window.print(), 90);
  };

  const hasFiles = shown.length > 0 || docs.some(d => d.url && !d.field) || files.some(f => f.for_permit);
  const downloadBundle = async () => {
    setZipBusy(true); setErr(null);
    setOkMsg(null);
    try {
      // ซองมีเฉพาะรูปที่อยู่บนใบ — รูปที่กด "ไม่ใส่ในใบ" ไม่ถูกส่ง
      const n = await db.downloadPermitBundle(cls, shown.map(({ p }) => p), docs, files);
      setOkMsg(`ดาวน์โหลดซองแล้ว — ${n} ไฟล์ + ข้อมูลสินค้า.txt`);
    } catch (e) { setErr(e.message ?? String(e)); }
    setZipBusy(false);
  };

  const refFiles = files.filter(f => f.kind === 'reference');
  const tisiText = docs.find(d => d.field === 'tisi')?.text ?? '';
  const setTisi = (v) => saveField('tisi', 'test', v);
  const noteOf = (kind) => docs.find(d => d.kind === kind && d.field === 'note')?.text ?? '';
  const setNote = (kind, v) => saveField('note', kind, v, kind);
  // upsert รายการข้อความ (note/tisi/lic_*/ref_*) ลง permit_docs — ค่าเดิม = ไม่เขียน (blur เฉย ๆ ไม่ยิง DB และไม่เลื่อนวันที่บันทึก)
  //   ref_* เก็บเวลาบันทึก (at) ไว้เตือนว่าเลขใบขนเป็นของรอบไหน
  const saveField = async (field, kind, v, keySuffix) => {
    const t = (v || '').trim();
    const cur = docs.find(d => d.field === field && d.kind === kind);
    if ((cur?.text ?? '') === t) return;
    const id = field + '-' + (keySuffix || kind);
    const item = { id, kind, field, text: t, ...(kind === 'ref' ? { at: new Date().toISOString() } : {}) };
    const next = docs.filter(d => !(d.field === field && d.kind === kind)).concat(t ? [item] : []);
    setDocs(next);
    try { await db.savePermitDocs(cls.id, next); } catch (er) { setErr(er.message ?? String(er)); }
  };
  const pickAgency = (v) => { agencyPicked.current = true; setAgency(v); if (canEdit) saveField('agency', 'meta', v); };

  // ── อ้างอิงการนำเข้ารอบนี้ ──
  const refOf = (f) => docs.find(d => d.kind === 'ref' && d.field === f)?.text ?? '';
  const refDocs = docs.filter(d => d.kind === 'ref' && d.text);
  // วันที่ที่โชว์ = ช่องที่ "เก่าสุด" — แก้แค่จำนวนรอบใหม่แล้วลืมเลขใบขน วันที่ต้องยังฟ้องว่าเลขใบขนเป็นของรอบก่อน
  //   (เดิมโชว์ล่าสุด = วันที่แก้จำนวนกลบเลขเก่า — รีวิว 30 ต.ค. 69) · วันต่างกัน = บอกทั้งสองวัน
  const thDate = (iso) => new Date(iso).toLocaleDateString('th-TH', { timeZone: 'Asia/Bangkok', day: 'numeric', month: 'short', year: 'numeric' });
  const refAts = refDocs.map(d => d.at).filter(Boolean).sort();
  const refAt = refAts.length ? thDate(refAts[0]) : '';
  const refAtLast = refAts.length ? thDate(refAts[refAts.length - 1]) : '';
  const clearRef = async () => {
    setRefAsk(false);
    const next = docs.filter(d => d.kind !== 'ref');
    setDocs(next); setRefEpoch(x => x + 1);
    try { await db.savePermitDocs(cls.id, next); } catch (er) { setErr(er.message ?? String(er)); }
  };
  const [decl, line, inv, bl, arrive, qty] = ['ref_decl', 'ref_line', 'ref_inv', 'ref_bl', 'ref_arrive', 'ref_qty'].map(refOf);
  const refParts = [
    (decl || line) && <span key="d">
      {decl && <>ใบขนสินค้าขาเข้าเลขที่ <b>{decl}</b></>}{decl && line ? ' ' : ''}{line && <>รายการที่ <b>{line}</b></>}
    </span>,
    inv && <span key="i">Invoice {inv}</span>,
    bl && <span key="b">{blText(bl)}</span>,
    arrive && <span key="a">นำเข้า {arrive}</span>,
  ].filter(Boolean);

  // ── ไฟฟ้า: กำลังไฟจากใบสินค้า หรือ ติ๊ก "ไม่ใช้ไฟฟ้า" ในหน้านี้ (ข้อความรับรองบนใบราชการ — ต้องติ๊กเอง ไม่เดาจาก "-") ──
  const noPower = docs.some(d => d.field === 'no_power' && d.text === '1');

  // ── ใบอนุญาตที่ได้รับแล้ว (เก็บผลลัพธ์ เช่น ใบ มอก. สมอ.) ──
  const licField = (f) => docs.find(d => d.kind === 'license' && d.field === f)?.text ?? '';
  const setLic = (f, v) => saveField(f, 'license', v, f);
  const licFiles = docs.filter(d => d.kind === 'license' && !d.field);   // แบบเก่า (url สาธารณะ)
  const licPf = files.filter(f => f.kind === 'license');
  const licStatus = licField('lic_status'), licNo = licField('lic_no'), licIssued = licField('lic_issued'), licExpiry = licField('lic_expiry');
  const daysToExpiry = licExpiry ? Math.round((new Date(licExpiry).setHours(0, 0, 0, 0) - new Date().setHours(0, 0, 0, 0)) / 86400000) : null;
  // วันที่บนใบ = วันที่เปิด/พิมพ์ (เวลาไทย) — เดิมเป็นช่องว่าง "วันที่ ____" ให้เขียนมือ
  const today = new Date().toLocaleDateString('th-TH', { timeZone: 'Asia/Bangkok', day: 'numeric', month: 'long', year: 'numeric' });
  // แถวที่กรอกในหน้านี้ — "กรอกแล้ว" = มีข้อความ / มีไฟล์ที่จะยื่น (ไฟล์อ้างอิงที่ไม่ติ๊ก = ไม่นับ) / เลข มอก.
  const kindOf = Object.fromEntries(DOC_KINDS.map(dk => {
    const items = docs.filter(d => d.kind === dk.k && !d.field);   // แบบเก่า (url สาธารณะ)
    // ไฟล์อ้างอิงจากใบสินค้าที่ติ๊กใส่ซองแล้ว → ขึ้นในแถว "ข้อมูลเพิ่มเติม" บนใบที่พิมพ์
    const pf = files.filter(f => f.kind === dk.k || (dk.k === 'other' && f.kind === 'reference' && f.for_permit));
    const note = noteOf(dk.k);
    const filled = !!note || items.length > 0 || pf.some(f => f.for_permit) || (dk.k === 'test' && !!tisiText);
    return [dk.k, { dk, items, pf, note, filled }];
  }));
  const fmtDate = (s) => { if (!s) return '—'; const [y, m, d] = String(s).slice(0, 10).split('-'); return d && m && y ? `${d}/${m}/${Number(y) + 543}` : s; };

  // ── แถวในตาราง ──
  const blankTag = <span className="pd-blank-tag noprint">ว่าง · ไม่พิมพ์</span>;
  // ข้อมูลจากใบสินค้า — แก้ที่หน้าสินค้า · need = ช่องสำคัญที่ว่างอยู่ โชว์บนจอให้รู้ว่าขาด (ไม่พิมพ์)
  const fieldRow = (label, val, { need = false, strong = false } = {}) => (
    hasVal(val)
      ? <tr key={label}><th>{label}</th><td className={strong ? 'pd-strong' : undefined}>{val}</td></tr>
      : need
      ? <tr key={label} className="noprint pd-blank"><th>{label}{blankTag}</th>
          <td className="pd-missing">ยังไม่มีข้อมูล — เติมได้ที่หน้าสินค้า (ปุ่ม “แก้ไข” ของสินค้านี้)</td></tr>
      : null
  );
  // ข้อมูลที่กรอกในหน้านี้ — บนจอเป็นช่องพิมพ์ · ตอนพิมพ์เป็นข้อความ (ขึ้นบรรทัดใหม่ = รายการจุด)
  const noteRow = (k) => {
    const { dk, items, pf, note, filled } = kindOf[k];
    if (!canEdit && !filled) return null;
    return (
      <tr key={k} className={filled ? undefined : 'noprint pd-blank'}>
        <th>
          {dk.label}
          {!filled && blankTag}
          {canFiles && <button type="button" className="pd-attach noprint" onClick={() => pick(k)} disabled={busy}
            title="แนบไฟล์ตัวจริง (ไม่บังคับ — ไฟล์อยู่ในซอง ZIP)">＋ แนบไฟล์</button>}
        </th>
        <td>
          {canEdit && <textarea className="pd-in noprint" defaultValue={note} placeholder={dk.ph}
            rows={Math.min(6, Math.max(1, note.split('\n').length))} onBlur={e => setNote(k, e.target.value)}/>}
          {note && <div className={`pd-print${canEdit ? '' : ' ro'}`}>{listOf(note, /\n/)}</div>}
          {k === 'test' && (canEdit || tisiText) && (
            <div className={`pd-tisi${tisiText ? '' : ' noprint'}`}>
              <span>เลข มอก.</span>
              {canEdit && <input className="pd-in pd-tisi-in noprint" defaultValue={tisiText}
                placeholder="เช่น 2432-2555 (ถ้ามี)" onBlur={e => setTisi(e.target.value)}/>}
              {tisiText && <b className={`pd-print-in${canEdit ? '' : ' ro'}`}>{tisiText}</b>}
            </div>
          )}
          {items.length + pf.length > 0 && (
            <div className="pa-files">
              {items.map(it =>
                <FileChip key={it.id} it={it} resolve={resolveImg} onRemove={canEdit ? () => removeDoc(it.id) : null}/>)}
              {pf.map(f => fileChip(f, f.kind !== 'reference'))}
            </div>
          )}
        </td>
      </tr>
    );
  };
  const [hsMain, hsStat] = hsParts(cls.hs_code);
  const origin = cls.origin_country
    && (ccName(cls.origin_country) !== cls.origin_country ? `${ccName(cls.origin_country)} (${cls.origin_country})` : cls.origin_country);

  return (
    <div className="page">
      <input ref={fileInput} type="file" style={{ display: 'none' }} onChange={onFile}/>
      <div className="page-h noprint">
        <button className="btn2" onClick={onBack}>← กลับ</button>
        <h2>เอกสารยื่นขอใบอนุญาต</h2>
        <span className="meta"><NameTh c={cls} missing/></span>
        <label className="permit-pick">ยื่นต่อ:&nbsp;
          <select value={agency} onChange={e => pickAgency(e.target.value)}>
            {AGENCIES.map(x => <option key={x.id} value={x.id}>{x.short}</option>)}
          </select>
        </label>
        <SignerEditor signer={signer} onSaved={reloadSigner}/>
        {hasFiles && !MOCK && (
          <button className="btn2 permit-zip" onClick={downloadBundle} disabled={zipBusy}
            title="รวมรูปที่อยู่บนใบ + ไฟล์ที่ติ๊ก “ใส่ในซองยื่น” เป็น ZIP ชุดเดียว ไปยื่นหน่วยงาน">
            {zipBusy ? 'กำลังรวม…' : '⬇ ไฟล์ที่จะยื่น (ZIP)'}</button>
        )}
        <button className="btn1" onClick={doPrint} disabled={printing}>
          {printing ? 'กำลังเตรียมรูป…' : 'พิมพ์ใบสรุป / PDF'}</button>
      </div>

      {err && <div className="actbar noprint"><span>ทำรายการไม่สำเร็จ: {err}</span>
        <button className="actbar-x" onClick={() => setErr(null)}>✕</button></div>}
      {okMsg && <div className="actbar ok noprint"><span>{okMsg}</span>
        <button className="actbar-x" onClick={() => setOkMsg(null)}>✕</button></div>}
      {canEdit && !canCost && (
        <div className="permit-mocknote noprint">
          ไฟล์แนบของสินค้าเปิดดูได้เฉพาะทีมจัดซื้อ/นำเข้า — ใบที่พิมพ์จากบัญชีนี้จะไม่มีรายการไฟล์แนบ
        </div>
      )}
      <div className="permit-hint noprint">
        <b>แผ่นด้านล่าง = หน้าตาที่พิมพ์ออกไปจริง</b> ·
        ① รูป 2–3 รูป พิมพ์คำอธิบายใต้รูปได้ (รูปไหนไม่ต้องการ กด “ไม่ใส่ในใบ”) ·
        ② เติมช่องในตาราง เช่น ขนาด / น้ำหนัก · แหล่งข้อมูล และเลขใบขนในกรอบ “อ้างอิงการนำเข้ารอบนี้” (ถ้ามี) ·
        ③ กด “พิมพ์ใบสรุป / PDF” — <b>แถวที่ยังว่างไม่ถูกพิมพ์</b> และย่อให้จบ A4 หน้าเดียวเอง ·
        ชื่อ/วัสดุ/วิธีใช้ แก้ที่หน้าสินค้า · ไฟล์ตัวจริงโหลดครบชุดที่ปุ่ม “ไฟล์ที่จะยื่น (ZIP)” (เฉพาะที่ติ๊ก <b>ใส่ในซองยื่น</b>)
      </div>
      {/* ไฟล์อ้างอิงจากใบสินค้า — อยู่ "นอก" แผ่น A4 โดยตั้งใจ: ของนอกกระดาษ = ไม่ถูกส่ง
          (เดิมวางในแผ่นแล้วติด noprint — บนจอดูเหมือนเป็นส่วนหนึ่งของเอกสารที่ยื่น ชวนเข้าใจผิด) */}
      {refFiles.length > 0 && (
        <div className="permit-refs noprint">
          <div className="permit-attach-t">ไฟล์อ้างอิงจากใบสินค้า ({refFiles.length}) — เลือกได้ว่าจะยื่นด้วยไหม</div>
          <div className="permit-refs-hint">
            ไฟล์ที่แนบไว้ในหน้าแก้ไขสินค้า <b>ยังไม่อยู่ในซองยื่น</b> จนกว่าจะติ๊ก “ใส่ในซองยื่น” ·
            ติ๊กแล้วจะขึ้นบนใบด้านล่างในแถว “ข้อมูลเพิ่มเติม” และอยู่ในซอง ZIP
          </div>
          <div className="pa-files">{refFiles.map(f => fileChip(f, false))}</div>
        </div>
      )}
      {MOCK && <div className="permit-mocknote noprint">โหมดตัวอย่าง — เพิ่มรูป/แนบไฟล์ได้จริงเมื่อเข้าใช้งานจริง (ปุ่มถูกปิดในตัวอย่าง)</div>}

      {/* อ้างอิงการนำเข้ารอบนี้ — ฟอร์มอยู่นอกแผ่น (แผ่นมีแค่กรอบเทาที่พิมพ์) · ของรอบนำเข้า ไม่ใช่ของสินค้า → มีวันที่ + ปุ่มล้าง */}
      {canEdit && (
        <div className="permit-ref noprint" key={refEpoch}>
          <div className="pr-h">
            <span className="pr-t">อ้างอิงการนำเข้ารอบนี้ <span className="pr-sub">· ไม่บังคับ — กรอกแล้วขึ้นในกรอบเทาบนใบ · จำนวนขึ้นในตาราง</span></span>
            {refDocs.length > 0 && (refAsk
              ? <span className="pr-ask">ล้างเลขอ้างอิงทั้งหมด?
                  <button type="button" className="fchip-yes" onClick={clearRef}>ล้าง</button>
                  <button type="button" className="fchip-no" onClick={() => setRefAsk(false)}>ไม่</button></span>
              : <button type="button" className="pr-clear" onClick={() => setRefAsk(true)}
                  title="ยื่นรอบใหม่ (ตู้/ใบขนใหม่) — ล้างเลขของรอบก่อนออกให้หมด">ล้าง (ยื่นรอบใหม่)</button>)}
          </div>
          <div className="pr-grid">
            {REF_FIELDS.map(r => (
              <label key={r.f} className="pr-f">
                <span>{r.label}</span>
                <input defaultValue={refOf(r.f)} placeholder={r.ph} onBlur={e => saveField(r.f, 'ref', e.target.value)}/>
              </label>
            ))}
          </div>
          {refDocs.length > 0 && (
            <div className="pr-warn">
              {refAt ? (refAtLast !== refAt
                ? <>บางช่องบันทึกไว้ตั้งแต่ <b>{refAt}</b> (แก้ล่าสุด {refAtLast}) · </>
                : <>บันทึกเมื่อ <b>{refAt}</b> · </>) : null}
              เลขเหล่านี้เป็นของการนำเข้ารอบนี้ — ถ้ายื่นรอบใหม่ ฝากกด “ล้าง (ยื่นรอบใหม่)” แล้วกรอกของรอบนั้น
            </div>
          )}
        </div>
      )}

      <div className="a4 permit-a4 pdoc" ref={sheetRef}>
        <div className="pd-hd">
          <div className="pd-co">{DOC_HEAD_TH.name} <span>({DOC_HEAD_TH.branch})</span></div>
          <div className="pd-coen">{DOC_HEAD_TH.nameEn}</div>
          <div className="pd-addr">{DOC_HEAD_TH.line}</div>
        </div>
        <div className="pd-h1">{SHEET_TITLE}</div>
        <div className="pd-to"><b>เรียน</b> {a.name}</div>
        {refParts.length > 0
          ? <div className="pd-refl">{refParts.map((x, i) => <Fragment key={i}>{i > 0 && ' · '}{x}</Fragment>)}</div>
          : canEdit && <div className="pd-refl-empty noprint">กรอบเลขใบขน / Invoice — กรอกได้ที่ “อ้างอิงการนำเข้ารอบนี้” ด้านบน (ไม่บังคับ · ว่าง = ไม่พิมพ์)</div>}

        <div className={`pd-photos${shown.length ? '' : ' noprint'}`}>
          {shown.map(({ p, i }) => (
            <figure className="pd-fig" key={p.url + i}>
              <img src={resolveImg(p.url)} alt={capOf(p)}/>
              {canEdit
                ? <>
                    <input className="pd-cap-in noprint" defaultValue={capOf(p)} placeholder="คำอธิบายรูป เช่น ขณะใช้งาน"
                      onBlur={e => setCaption(i, e.target.value)}/>
                    {capOf(p) && <figcaption className="pd-print">{capOf(p)}</figcaption>}
                  </>
                : capOf(p) && <figcaption>{capOf(p)}</figcaption>}
              {canEdit && (
                <div className="pd-fig-act noprint">
                  {rmAsk === p.url
                    ? <span className="pd-ask">เอาออกจากสินค้า?
                        <button type="button" className="fchip-yes" onClick={() => removePhoto(i)}>เอาออก</button>
                        <button type="button" className="fchip-no" onClick={() => setRmAsk(null)}>ไม่</button></span>
                    : <>
                        <button type="button" onClick={() => setOnSheet(i, false)}
                          title="ไม่พิมพ์รูปนี้ในใบที่ยื่น — รูปยังอยู่ในสินค้า">ไม่ใส่ในใบ</button>
                        <button type="button" className="pd-rm" onClick={() => setRmAsk(p.url)} title="เอารูปออกจากสินค้า">✕</button>
                      </>}
                </div>
              )}
            </figure>
          ))}
          {canEdit && canCost && (
            // ครบแถวแล้ว (3 · 6 …) → ปุ่มเป็นแถบบางเต็มแถว ไม่กินช่องรูปทั้งแถวบนจอ
            <button type="button" className={`pd-add noprint${shown.length && shown.length % 3 === 0 ? ' slim' : ''}`}
              onClick={() => pick('photo')} disabled={busy}>
              <span>＋</span>เพิ่มรูป
            </button>
          )}
        </div>
        {!shown.length && !canEdit && <div className="pd-nophoto noprint">ยังไม่มีรูป</div>}
        {canEdit && hidden.length > 0 && (
          <div className="pd-hidden noprint">
            <span>รูปที่ไม่ใส่ในใบ ({hidden.length})</span>
            {hidden.map(({ p, i }) => (
              <span className="pd-hidden-it" key={p.url + i}>
                <img src={p.url} alt=""/>
                <button type="button" onClick={() => setOnSheet(i, true)}>ใส่ในใบ</button>
              </span>
            ))}
          </div>
        )}

        <table className="pd-f"><tbody>
          {fieldRow('ชื่อสินค้า (ไทย)', cls.name_th, { need: true, strong: true })}
          {fieldRow('ชื่อสินค้า (อังกฤษ)', cls.name_en, { need: true })}
          {fieldRow('ยี่ห้อ / รุ่น', [cls.brand, cls.sku && `รุ่น ${cls.sku}`].filter(Boolean).join(' · '))}
          {fieldRow('พิกัดศุลกากร', hsMain && <><b>{hsMain}</b>{hsStat && ` (รหัสสถิติ ${hsStat})`}</>, { need: true })}
          {fieldRow('ลักษณะสินค้า', cls.what_is_it, { need: true })}
          {fieldRow('วิธีใช้งาน', cls.usage_desc, { need: true })}
          {fieldRow('วัสดุ', hasVal(cls.material) && listOf(cls.material, /\s*;\s*|\n/), { need: true })}
          {noteRow('spec')}
          {hasVal(cls.power_spec)
            ? <tr><th>ไฟฟ้า</th><td>{cls.power_spec}</td></tr>
            : (noPower || canEdit) && (
              <tr className={noPower ? undefined : 'noprint pd-blank'}>
                <th>ไฟฟ้า{!noPower && blankTag}</th>
                <td>
                  {canEdit && (
                    <label className="pd-check noprint">
                      <input type="checkbox" checked={noPower}
                        onChange={e => saveField('no_power', 'meta', e.target.checked ? '1' : '')}/>
                      {NO_POWER_TEXT}{!noPower && <span className="pd-check-hint"> — ติ๊กถ้าสินค้านี้ไม่ใช้ไฟฟ้า</span>}
                    </label>
                  )}
                  {noPower && <div className={`pd-print${canEdit ? '' : ' ro'}`}>{NO_POWER_TEXT}</div>}
                </td>
              </tr>
            )}
          {cls.is_dangerous_goods && fieldRow('สินค้าอันตราย (DG)', 'ใช่ — แบตเตอรี่ / แก๊ส / วัตถุอันตราย')}
          {fieldRow('ประเทศผู้ผลิต', origin, { need: true })}
          {fieldRow('จำนวนที่นำเข้า', qty)}
          {noteRow('catalog')}
          {noteRow('manual')}
          {noteRow('test')}
          {noteRow('other')}
        </tbody></table>

        {/* ช่องลงชื่อ = ที่ให้เซ็นจริง · ชื่อ/ตำแหน่ง/วันที่ เติมเอง ไม่เหลือช่องว่างให้กรอกมือ */}
        <div className="pd-sign">
          <div>ลงชื่อ ........................................................ ผู้นำเข้า</div>
          {signer?.full_name && <div>( {signer.full_name} )</div>}
          {signer?.job_title && <div>ตำแหน่ง {signer.job_title}</div>}
          <div>ในนาม {DOC_HEAD_TH.name}</div>
          <div>วันที่ {today}</div>
        </div>
        <div className="a4-foot noprint">
          เอกสารนี้สรุปข้อมูลสินค้าเพื่อประกอบคำขอต่อหน่วยงาน — แบบฟอร์ม/เอกสารที่ต้องใช้จริงยึดตามที่หน่วยงานกำหนด · สร้างจาก INBOUNDpoint
        </div>
      </div>

      {/* ── ผลจากหน่วยงาน (สมอ. = หนังสือรับรองไม่เข้าข่าย / ใบอนุญาต / หนังสือรับรอง) ──
          อยู่ "นอก" แผ่น A4 และไม่พิมพ์ (30 ต.ค. 69 user: "เอกสารที่ยื่นควรมีแค่ส่วนที่เราใส่ข้อมูล ไม่ต้องมีช่องข้อมูลที่ไม่ได้ใส่ข้อมูลจาก สมอ.")
          เป็นบันทึกของเราเองว่าหน่วยงานตอบว่าอะไร — เดิมอยู่ในแผ่น ใบที่ยื่นเลยมีช่องว่าง "สถานะ — · เลขที่ — · วันที่ออก —"
          ไฟล์ผลจากหน่วยงานแนบแบบไม่ใส่ซองยื่น (ติ๊กเองได้) — ซองคือของที่เราส่งไป ไม่ใช่ของที่เขาตอบกลับ */}
      <div className={`permit-license outside noprint${licStatus === CLEARED ? ' cleared' : ''}${canEdit ? '' : ' ro'}`}>
        <div className="pl-h">
          <span className="pl-h-t">เอกสารรับรอง / ผลพิจารณา จาก {a.short} <span className="pl-h-sub">· บันทึกของเรา ไม่พิมพ์ลงใบที่ยื่น</span></span>
          {canFiles && <button className="pa-add noprint" onClick={() => pick('license')} disabled={busy}>＋ แนบไฟล์เอกสาร</button>}
        </div>
        <div className="pl-fields">
          <div className="pl-f pl-status">
            <span className="pl-l">สถานะ:</span>
            {canEdit && (
              <select className="pl-in noprint" defaultValue={licStatus}
                onChange={e => setLic('lic_status', e.target.value)}>
                <option value="">— เลือกสถานะ —</option>
                {LIC_STATUS.map(s => <option key={s} value={s}>{s}</option>)}
              </select>
            )}
            <b className={`pl-print ${licStatus === CLEARED ? 'go' : ''}`}>{licStatus || '—'}</b>
            {licStatus === CLEARED && <span className="pl-badge ok noprint">✓ นำเข้าได้เลย</span>}
          </div>
          <div className="pl-f">
            <span className="pl-l">เลขที่หนังสือ / ใบรับรอง:</span>
            {canEdit && <input className="pl-in noprint" defaultValue={licNo} placeholder="เช่น อก 0709/... (ถ้ามี)"
              onBlur={e => setLic('lic_no', e.target.value)}/>}
            <b className="pl-print">{licNo || '—'}</b>
          </div>
          <div className="pl-f">
            <span className="pl-l">วันที่ออก:</span>
            {canEdit && <input className="pl-in noprint" type="date" defaultValue={licIssued}
              onBlur={e => setLic('lic_issued', e.target.value)}/>}
            <b className="pl-print">{fmtDate(licIssued)}</b>
          </div>
          <div className="pl-f">
            <span className="pl-l">วันหมดอายุ (ถ้ามี):</span>
            {canEdit && <input className="pl-in noprint" type="date" defaultValue={licExpiry}
              onBlur={e => setLic('lic_expiry', e.target.value)}/>}
            <b className="pl-print">{fmtDate(licExpiry)}</b>
            {daysToExpiry != null && (daysToExpiry < 0
              ? <span className="pl-badge exp">หมดอายุแล้ว {-daysToExpiry} วัน</span>
              : daysToExpiry <= 60
              ? <span className="pl-badge warn">ใกล้หมดอายุ (อีก {daysToExpiry} วัน)</span>
              : <span className="pl-badge ok">ใช้ได้อีก {daysToExpiry} วัน</span>)}
          </div>
        </div>
        {licFiles.length + licPf.length > 0
          ? <div className="pa-files">
              {licFiles.map(it =>
                <FileChip key={it.id} it={it} resolve={resolveImg} onRemove={canEdit ? () => removeDoc(it.id) : null}/>)}
              {licPf.map(f => fileChip(f, true))}
            </div>
          : <div className="pl-empty">— ยังไม่มีไฟล์เอกสาร (แนบหนังสือรับรอง/ใบอนุญาตเมื่อได้รับจากหน่วยงานแล้ว) —</div>}
      </div>
    </div>
  );
}
